import React, { useState } from 'react';
/**
 * AuthGuard — Authentication + Onboarding screen rendering
 *
 * Extracted from App.tsx in R34 (DDD Fase 6 — App Shell decomposition).
 * Renders WelcomeScreen, LoginScreen, or RegisterScreen when the user
 * is not authenticated.
 *
 * After authentication, if `hasCompletedOnboarding` is false/undefined,
 * the OnboardingWizard is shown:
 * - Learner → 2-staps (welkom + groep/vak-keuze) → home
 * - Parent  → 1-staps (welkom) → portaal empty-state met inline koppelcode
 * - Teacher → 1-staps (welkom) → portaal empty-state met inline klas-aanmaak
 * - Admin   → onboarding wordt overgeslagen
 *
 * Alle child/classroom-linking gebeurt voortaan in de portal-empty-states,
 * niet meer in de wizard (Sprint 2.8 — zie AUTH_ONBOARDING_DOMAIN_RC_AUDIT_2026.md §23).
 */
import { WelcomeScreen, LoginScreen, RegisterScreen, OnboardingWizard, type AccountsState, type Account, type OnboardingStepState } from '../accounts';
import type { CreateAccountDetails, CreateAccountOptions } from './store/types';
interface AuthGuardProps {
  showWelcome: boolean;
  currentNavView: string;
  accounts: AccountsState;
  activeAccount: Account | null;
  onLoadDemo: () => void;
  isLoadingDemo?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onLoginSuccess: (accountId: string) => void;
  onBack: () => void;
  createAccount: (role: 'learner' | 'teacher' | 'parent' | 'admin', details: CreateAccountDetails, options?: CreateAccountOptions) => string;
  // Onboarding callbacks
  onCompleteOnboarding: (stepState?: OnboardingStepState) => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
  children: React.ReactNode;
}
export function AuthGuard({
  showWelcome,
  currentNavView,
  accounts,
  activeAccount,
  onLoadDemo,
  isLoadingDemo,
  onNavigateToLogin,
  onNavigateToRegister,
  onLoginSuccess,
  onBack,
  createAccount,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass,
  children
}: AuthGuardProps): React.ReactElement {
  const [onboardingError, setOnboardingError] = useState<string | undefined>();
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  // ── Pre-auth screens ───────────────────────────────────────
  if (showWelcome) {
    return <WelcomeScreen onLoadDemo={onLoadDemo} isLoadingDemo={isLoadingDemo} onLogin={onNavigateToLogin} onRegister={onNavigateToRegister} data-id="element-644" />;
  }
  if (currentNavView === 'login') {
    return <LoginScreen onLoginSuccess={onLoginSuccess} onBack={onBack} onNavigateToRegister={onNavigateToRegister} accounts={accounts} data-id="element-645" />;
  }
  if (currentNavView === 'register') {
    return <RegisterScreen onRegisterSuccess={onLoginSuccess} onBack={onBack} onNavigateToLogin={onNavigateToLogin} accounts={accounts} createAccount={createAccount} data-id="element-646" />;
  }
  // ── Post-auth onboarding ───────────────────────────────────
  // Show onboarding for newly registered accounts that haven't completed it.
  // Demo accounts and admins skip onboarding entirely.
  if (activeAccount && !activeAccount.hasCompletedOnboarding && !activeAccount.isDemo && activeAccount.role !== 'admin') {
    return <OnboardingWizard account={activeAccount} accounts={accounts} onCompleteOnboarding={onCompleteOnboarding} onNavigateToModule={onNavigateToModule} onNavigateToMyClass={onNavigateToMyClass} onJoinClassroom={() => ({
      success: false
    })} // Stubbed for backwards compatibility
    onLinkChild={() => ({
      success: false
    })} // Stubbed for backwards compatibility
    onCreateClassroom={() => {}} // Stubbed for backwards compatibility
    data-id="element-647" />;
  }
  return <>{children}</>;
}